/**
 * Centralized filter configuration for TechnicalAlertsTable
 * Handles filter dropdown rendering for server-provided filter values
 */

import React from 'react';
import TableFilterDropdown from '../shared/TableFilterDropdown';
import { createFilterOptions } from './DataTransformationLayer';

/**
 * Create a filter dropdown renderer for server-provided filter values
 * @param {string} filterKey - The key to access filter values (e.g., 'storeClusterId')  
 * @param {Object} filterValues - Server-provided filter values object
 * @param {string} translationKey - Translation key for the column name
 * @param {boolean} loading - Whether filter values are loading
 * @param {Function} t - Translation function
 */
export const createFilterDropdown = (filterKey, filterValues, translationKey, loading, t) => {
  return (props) => {
    // Get server-provided values for this filter using the serverKey mapping
    const serverKey = FILTER_CONFIG[filterKey]?.serverKey || filterKey;
    const serverValues = filterValues[serverKey] || [];
    
    // Use centralized filter transformation  
    // Use serverKey for filter type detection to handle products correctly
    const values = createFilterOptions(serverValues, serverKey, t);
    
    return (
      <TableFilterDropdown
        type="checkbox"
        values={values}
        columnName={t(translationKey)}
        loading={loading}
        {...props}
      />
    );
  };
};

/**
 * Filter configuration for each column type
 * Maps frontend filter keys to their translation keys and server properties
 */
export const FILTER_CONFIG = {
  sector: {
    translationKey: 'sector',
    serverKey: 'sector'
  },
  group: {
    translationKey: 'group',
    serverKey: 'group'
  },
  location: {
    translationKey: 'location',
    serverKey: 'location'
  },
  machineId: {
    translationKey: 'machineId', 
    serverKey: 'machineId'
  },
  productName: {
    translationKey: 'productName', // Keep same translation key for UI
    serverKey: 'products' // But server expects 'products' key
  },
  weeksSince: {
    translationKey: 'weeksSince',
    serverKey: 'weeksSince'
  },
  alertType: {
    translationKey: 'alertType',
    serverKey: 'alertType'
  },
  status: {
    translationKey: 'status',
    serverKey: 'status'  // Server returns data under 'status' key
  },
  actions: {
    translationKey: 'status',  // Both actions and status show the same data
    serverKey: 'status'  // Both map to the same server key
  },
  route: {
    translationKey: 'route',
    serverKey: 'routes'
  }
};
